import {
  faCalendarCheck,
  faShop,
  faSmog,
  faDollarSign,
  faBox,
  faTriangleExclamation,
  faCircleCheck,
  faCircleInfo,
  faPause,
  faSpinner,
  faClipboardCheck
} from "@fortawesome/free-solid-svg-icons";

import { mainTheme } from "@aclymatepackages/themes";

import {
  faPodiumStar,
  faQuestion,
  faPiggyBank,
  faMeterBolt,
  faFileArrowUp
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";

import { bomProductsFieldOptions } from "./products";

export const transactionsFieldOptions = [
  {
    label: "Date",
    value: "date",
    description: "When did this transaction happen?",
    icon: faCalendarCheck,
    required: true,
    type: "string"
  },
  {
    label: "Vendor",
    value: "vendor",
    description: "Who did you buy it from?",
    icon: faShop,
    required: true,
    type: "string"
  },
  {
    label: "Amount",
    value: "amount",
    description: "How much did it cost?",
    icon: faDollarSign,
    required: true,
    type: "number"
  },
  {
    label: "Category",
    value: "category",
    description: "(Optional) What type of purchase was it?",
    icon: faSmog,
    required: false,
    type: "string"
  }
];

export const shippingFieldOptions = [
  { label: "Date", value: "date", type: "string" },
  { label: "Weight", value: "weight", type: "number" },
  { label: "Weight Units", value: "weightUnits", type: "string" },
  { label: "Sender Postal Code", value: "senderPostalCode", type: "string" },
  {
    label: "Recipient Postal Code",
    value: "recipientPostalCode",
    type: "string"
  },
  { label: "Recipient Country", value: "recipientCountry", type: "string" },
  { label: "Service Type", value: "serviceType", type: "string" },
  { label: "Miles", value: "miles", type: "number" },
  { label: "Kilometers", value: "kilometers", type: "number" },
  { label: "Direction", value: "direction", type: "string" }
];

// required flags mirror components/hooks/eventAttendeesCsvUploader.js's
// requiredFields -- only city/state/country/zipCode are mandatory for this
// upload; transportationMethod is optional and can legitimately be absent.
export const eventAttendeesFieldOptions = [
  { label: "City", value: "city", required: true, type: "string" },
  { label: "State", value: "state", required: true, type: "string" },
  { label: "Zip Code", value: "zipCode", required: true, type: "string" },
  { label: "Country", value: "country", required: true, type: "string" },
  {
    label: "Transportation Method",
    value: "transportationMethod",
    required: false,
    type: "string"
  }
];

export const waterCsvFieldOptions = [
  { label: "Date", value: "dateCommon", type: "string" },
  { label: "Quantity", value: "quantity", type: "number" },
  { label: "Unit", value: "unit", type: "string" },
  { label: "Wastewater %", value: "wastewaterPercentage", type: "number" }
];

export const steamCsvFieldOptions = [
  { label: "Date", value: "dateCommon", type: "string" },
  { label: "Quantity", value: "quantity", type: "number" },
  { label: "Unit", value: "unit", type: "string" }
];

export const chillWaterCsvFieldOptions = [
  { label: "Date", value: "dateCommon", type: "string" },
  { label: "kWh", value: "kwh", type: "number" }
];

export const refrigerantsCsvFieldOptions = [
  { label: "Date", value: "dateCommon", type: "string" },
  { label: "Quantity", value: "quantity", type: "number" },
  { label: "Unit", value: "unit", type: "string" },
  { label: "Refrigerant Type", value: "refrigerantType", type: "string" }
];

export const utilitiesCsvFieldOptions = [
  { label: "Date", value: "dateCommon", type: "string" },
  { label: "Electricity (kwh)", value: "unitKwh", type: "number" },
  { label: "Gas (therms)", value: "unitTherms", type: "number" },
  { label: "Gas (CCF)", value: "unitCcf", type: "number" },
  { label: "Gas (MCF)", value: "unitMcf", type: "number" },
  { label: "Gas (Cubic Meters)", value: "unitCubicMeters", type: "number" },
  { label: "Gas (Gallons Propane)", value: "unitGallonsPropane", type: "number" }
];

// office_csv (ticket #412) is one document_family spanning four subcategories
// (purchased-water/steam/chill-water/refrigerants), each with its own field
// shape -- this union covers every column any of the four can show, since
// SMART_UPLOAD_FIELD_OPTIONS_BY_FAMILY keys by family alone, not subcategory.
export const officeCsvFieldOptions = [
  ...waterCsvFieldOptions,
  ...steamCsvFieldOptions,
  ...chillWaterCsvFieldOptions,
  ...refrigerantsCsvFieldOptions
].filter(
  ({ value }, index, all) =>
    all.findIndex((option) => option.value === value) === index
);

// Ticket #372 (Import-path consolidation): transactions/shipping-transactions/
// bom-products tasks now write type: "smart-upload" with a document_family
// row field instead of their old type-keyed values -- every place that
// branched on `type` alone (this map, getTaskTypesDetails, getFileNameFromPath
// below) needs the same document_family branch or it silently regresses for
// these three task types the moment their type value changed.
//
// A Map, not a plain object (Phase 3 PR review finding) -- a document_family
// value like "constructor" would otherwise resolve to Object.prototype's own
// built-in on a plain-object lookup instead of correctly falling through.
// Exported so AccountActivity.js's column-header builder can reuse the same
// map rather than keeping its own separately-maintained copy.
export const SMART_UPLOAD_FIELD_OPTIONS_BY_FAMILY = new Map([
  ["transactions_csv", transactionsFieldOptions],
  ["shipping_transactions", shippingFieldOptions],
  ["bom_products_csv", bomProductsFieldOptions],
  ["utilities_csv", utilitiesCsvFieldOptions],
  ["office_csv", officeCsvFieldOptions]
]);

export const getFieldOptionsByTaskType = (taskType, documentFamily) => {
  if (taskType === "vendors" || taskType === "accounting") {
    return transactionsFieldOptions;
  }
  if (
    taskType === "smart-upload" &&
    SMART_UPLOAD_FIELD_OPTIONS_BY_FAMILY.has(documentFamily)
  ) {
    return SMART_UPLOAD_FIELD_OPTIONS_BY_FAMILY.get(documentFamily);
  }
  if (taskType === "shipping-transactions") {
    return shippingFieldOptions;
  }
  if (taskType === "event-attendees-csv") {
    return eventAttendeesFieldOptions;
  }
  if (taskType === "purchased-water-csv") {
    return waterCsvFieldOptions;
  }
  if (taskType === "steam-csv") {
    return steamCsvFieldOptions;
  }
  if (taskType === "chill-water-csv") {
    return chillWaterCsvFieldOptions;
  }
  if (taskType === "refrigerants-csv") {
    return refrigerantsCsvFieldOptions;
  }
  return [];
};

export const getExpectedTypeForField = (fieldOptions, field) => {
  const found = fieldOptions.find((opt) => opt.value === field);

  return found?.type;
};

export const isFieldRequired = (fieldOptions, field) => {
  const found = fieldOptions.find((opt) => opt.value === field);

  return found?.required ?? true;
};

export const isValueValidForType = (value, type, required = true) => {
  if (!required && (value === null || value === undefined)) {
    return true;
  }
  if (type === "number") {
    return value === "" || !isNaN(Number(value));
  }
  if (type === "string") {
    return typeof value === "string";
  }
  return true;
};

export const getTypeErrorMessage = (type) => {
  if (type === "number") {
    return "Expected a numeric value.";
  }
  if (type === "string") {
    return "Expected a string value.";
  }
  return undefined;
};

// Ticket #372: the same document_family branch getFieldOptionsByTaskType
// above needs, for the friendly name/icon/color a migrated task's Chip shows
// -- keyed by a thunk (not a plain object) since each entry references
// fontSize/palette, which aren't known until getTaskTypesDetails is called.
// A Map, not a plain object (Phase 3 PR review finding) -- see
// SMART_UPLOAD_FIELD_OPTIONS_BY_FAMILY above for why.
const getSmartUploadLegacyDetails = (documentFamily, fontSize, palette) => {
  const detailsByFamily = new Map([
    [
      "transactions_csv",
      () => ({
        name: "Transactions",
        Icon: <PlaylistAddCheckIcon fontSize={fontSize} color="white" />,
        color: palette["spend-based"].main
      })
    ],
    [
      "shipping_transactions",
      () => ({
        name: "Shipping Transactions",
        Icon: (
          <LocalShippingIcon fontSize={fontSize} style={{ color: "white" }} />
        ),
        color: palette.shipping.main
      })
    ],
    [
      "bom_products_csv",
      () => ({
        name: "Supplier Products",
        Icon: (
          <FontAwesomeIcon icon={faBox} fontSize={fontSize} color="white" />
        ),
        color: palette.primary.main
      })
    ],
    [
      "utilities_csv",
      () => ({
        name: "Utilities CSV",
        Icon: (
          <FontAwesomeIcon
            icon={faMeterBolt}
            fontSize={fontSize}
            color="white"
          />
        ),
        color: palette.utilities.main
      })
    ],
    [
      "office_csv",
      () => ({
        name: "Office Usage CSV",
        Icon: (
          <FontAwesomeIcon
            icon={faFileArrowUp}
            fontSize={fontSize}
            color="white"
          />
        ),
        color: palette.primary.main
      })
    ]
  ]);

  return detailsByFamily.get(documentFamily)?.();
};

export const getTaskTypesDetails = ({
  taskType,
  documentFamily,
  fontSize = "small"
}) => {
  const { palette } = mainTheme;

  switch (taskType) {
    case "transactions":
      return {
        name: "Transactions",
        Icon: <PlaylistAddCheckIcon fontSize={fontSize} color="white" />,
        color: palette["spend-based"].main
      };

    case "shipping-transactions":
      return {
        name: "Shipping Transactions",
        Icon: (
          <LocalShippingIcon fontSize={fontSize} style={{ color: "white" }} />
        ),
        color: palette.shipping.main
      };

    case "event-attendees-csv":
      return {
        name: "Event Attendees",
        Icon: (
          <FontAwesomeIcon
            icon={faPodiumStar}
            fontSize={fontSize}
            color="white"
          />
        ),
        color: palette.events.main
      };

    case "plaid-transactions":
      return {
        name: "Plaid Transactions",
        Icon: (
          <FontAwesomeIcon
            icon={faPiggyBank}
            fontSize={fontSize}
            color="white"
          />
        ),
        color: palette["spend-based"].dark
      };

    case "utilities-api":
      return {
        name: "Utilities API",
        Icon: (
          <FontAwesomeIcon
            icon={faMeterBolt}
            fontSize={fontSize}
            color="white"
          />
        ),
        color: palette.utilities.main
      };

    case "purchased-water-csv":
      return {
        name: "Purchased Water CSV",
        Icon: (
          <FontAwesomeIcon
            icon={faFileArrowUp}
            fontSize={fontSize}
            color="white"
          />
        ),
        color: palette["purchased-water"].main
      };

    case "steam-csv":
      return {
        name: "Steam CSV",
        Icon: (
          <FontAwesomeIcon
            icon={faFileArrowUp}
            fontSize={fontSize}
            color="white"
          />
        ),
        color: palette.steam.main
      };

    case "chill-water-csv":
      return {
        name: "Chill Water CSV",
        Icon: (
          <FontAwesomeIcon
            icon={faFileArrowUp}
            fontSize={fontSize}
            color="white"
          />
        ),
        color: palette["chill-water"].main
      };

    case "refrigerants-csv":
      return {
        name: "Refrigerants CSV",
        Icon: (
          <FontAwesomeIcon
            icon={faFileArrowUp}
            fontSize={fontSize}
            color="white"
          />
        ),
        color: palette.refrigerants.main
      };

    case "smart-upload": {
      const legacyDetails = getSmartUploadLegacyDetails(
        documentFamily,
        fontSize,
        palette
      );
      if (legacyDetails) {
        return legacyDetails;
      }

      return {
        name: "Smart Upload",
        Icon: (
          <FontAwesomeIcon
            icon={faFileArrowUp}
            fontSize={fontSize}
            color="white"
          />
        ),
        color: palette.primary.main
      };
    }

    default:
      return {
        name: "Unknown Task Type",
        Icon: (
          <FontAwesomeIcon
            icon={faQuestion}
            fontSize={fontSize}
            color="white"
          />
        ),
        color: palette.secondary.grayscale
      };
  }
};

export const formatTimestamp = (timestamp) => {
  if (!timestamp) {
    return "";
  }

  const date = timestamp?.seconds
    ? new Date(timestamp.seconds * 1000)
    : new Date(timestamp);

  if (isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat("default", {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    hour12: true
  }).format(date);
};

export const getTaskStatusDetails = (
  { status, totalTasks, totalTasksProcessed },
  numErroredTaskDataRows,
  palette,
  numNeedsAttentionRows = 0
) => {
  if (status === "finished" && !!numErroredTaskDataRows) {
    return {
      icon: faTriangleExclamation,
      color: palette.error.main,
      summary: `We processed ${totalTasks} rows of data but were unable to process ${numErroredTaskDataRows} rows of data.`,
      spin: false
    };
  }

  // Smart Upload rows that extracted no usable lines (no_activity_found, an
  // AI-call hiccup, a too-large document, or content the model judged
  // unreadable) are a distinct, non-error terminal state -- never counted in
  // numErroredTaskDataRows (ticket #376 spec) -- but a batch made up only of
  // these still needs a visible signal, not the same plain "no errors"
  // checkmark a fully clean batch gets.
  if (status === "finished" && !!numNeedsAttentionRows) {
    return {
      icon: faCircleInfo,
      color: palette.warning.main,
      summary: `We processed ${totalTasks} rows of data; ${numNeedsAttentionRows} need your review to continue.`,
      spin: false
    };
  }

  if (status === "finished") {
    return {
      icon: faCircleCheck,
      color: palette.secondary.main,
      summary: `We processed ${totalTasks} rows of data without any errors`,
      spin: false
    };
  }

  // Smart Upload batches wait here on a reviewer after extraction (ticket
  // #378); they are not errors and not finished until every file is resolved.
  if (status === "ready_for_review") {
    return {
      icon: faClipboardCheck,
      color: palette.primary.main,
      summary: `Ready to review — we read ${totalTasks ?? 0} ${
        totalTasks === 1 ? "file" : "files"
      } and they're waiting for your confirmation.`,
      spin: false
    };
  }

  if (status === "partially_reviewed") {
    return {
      icon: faClipboardCheck,
      color: palette.warning.main,
      summary:
        "Review in progress — some lines are still waiting for your confirmation.",
      spin: false
    };
  }

  // A migrated CSV's "Confirm N ready lines" runs server-side in the
  // background (ticket #393); review stays open while it works.
  if (status === "bulk_committing") {
    return {
      icon: faSpinner,
      color: palette.primary.main,
      summary:
        "Confirming… your ready lines are being recorded in the background.",
      spin: true
    };
  }

  if (status === "paused") {
    return {
      icon: faPause,
      color: palette.secondary.grayscale,
      summary: "Waiting on another process to complete before continuing.",
      spin: false
    };
  }

  if (status === "running") {
    return {
      icon: faSpinner,
      color: palette.primary.main,
      summary: `Processing data (${totalTasksProcessed ?? 0}/${totalTasks ?? 0})`,
      spin: true
    };
  }

  if (status === "uploading") {
    return {
      icon: faSpinner,
      color: palette.primary.main,
      summary: "Uploading necessary data before starting process",
      spin: true
    };
  }

  return {
    icon: faQuestion,
    color: palette.secondary.grayscale,
    summary: "Unknown status type",
    spin: false
  };
};

// Ticket #372: transactions_csv/shipping_transactions were "transactions"/
// "shipping-transactions" respectively before this migration -- both raw-
// doc-name-in-path types above -- so a smart-upload task of either family
// needs the same -2 segment treatment or its display regresses to the
// literal "processed-data.csv" filename. bom_products_csv wasn't in this
// list even as "bom-products" pre-migration, so it needs no addition here.
// utilities_csv/office_csv (ticket #412) are new to the shared default-upload
// path (csvUploader.js's own storagePath/raw-data.csv/processed-data.csv
// shape) rather than carrying over an old literal-filename path, so they join
// this set too -- same reasoning as transactions_csv/shipping_transactions.
const RAW_DOC_NAME_SMART_UPLOAD_FAMILIES = new Set([
  "transactions_csv",
  "shipping_transactions",
  "utilities_csv",
  "office_csv"
]);

export const getFileNameFromPath = (filePath, type, documentFamily) => {
  if (!filePath || typeof filePath !== "string") {
    return "This task has no file path";
  }

  const parts = filePath.split("/");
  const usesRawDocNameSegment =
    ["transactions", "shipping-transactions", "event-attendees-csv"].includes(
      type
    ) ||
    (type === "smart-upload" &&
      RAW_DOC_NAME_SMART_UPLOAD_FAMILIES.has(documentFamily));

  return parts[parts.length - (usesRawDocNameSegment ? 2 : 1)];
};
